import { Injectable } from '@angular/core';
import { grpc } from '@improbable-eng/grpc-web';
import { environment } from '@mzic/mzic-environments';
import { Observable } from 'rxjs';

// Import the generated protobuf classes and types
import {
  CashoutStatusRequest,
  CashoutStatusResponse,
} from '../generated/cashout_pb';

// Re-export for convenience
export { CashoutStatusRequest, CashoutStatusResponse };

// Domain models for mapping protobuf responses
export interface CashoutStatusDomainResponse {
  taskId: string;
  status: CashoutStatusEnum;
  reasonForRejection?: string;
  transactionHash?: string;
}

export enum CashoutStatusEnum {
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  DELAYED = 'DELAYED',
  FAILED_INVALID_PIX_KEY = 'FAILED_INVALID_PIX_KEY',
  UNKNOWN = 'UNKNOWN',
}

/**
 * GRPC Service for monitoring cashout operations
 * Similar to Flutter's CashoutStreamRepository
 *
 * This implementation uses the generated protobuf classes for proper binary serialization
 */
@Injectable({
  providedIn: 'root',
})
export class CashoutGrpcService {
  private readonly grpcHost: string;

  constructor() {
    // Parse the gRPC URL from environment
    const url = new URL(environment.grpcUrl);
    this.grpcHost = `${url.protocol}//${url.host}`;
  }

  /**
   * Map status string to enum
   * Similar to Flutter's CashoutStatusEnum.fromString()
   */
  private mapStatusEnum(status: string): CashoutStatusEnum {
    switch (status?.toUpperCase()) {
      case 'SUCCESS':
        return CashoutStatusEnum.SUCCESS;
      case 'FAILED':
        return CashoutStatusEnum.FAILED;
      case 'DELAYED':
        return CashoutStatusEnum.DELAYED;
      case 'FAILED_INVALID_PIX_KEY':
        return CashoutStatusEnum.FAILED_INVALID_PIX_KEY;
      default:
        return CashoutStatusEnum.UNKNOWN;
    }
  }

  /**
   * Create metadata for gRPC calls
   * Similar to Flutter's CallOptions metadata
   */
  private createMetadata(): grpc.Metadata {
    const metadata = new grpc.Metadata();

    // Add authentication token
    const token = localStorage.getItem('access_token');
    if (token) {
      metadata.set('authorization', `Bearer ${token}`);
    }

    // Add client headers
    metadata.set('x-client-type', 'web');

    return metadata;
  }

  /**
   * Monitor cashout status using GRPC streaming
   * Similar to Flutter's monitorCashoutStatus in repository
   * @param taskId The task ID from the cashout initiation
   * @returns Observable stream of cashout status updates
   */
  monitorCashoutStatus(
    taskId: string,
  ): Observable<CashoutStatusDomainResponse> {
    console.log('[CashoutGrpcService] Starting monitoring for taskId:', taskId);

    return new Observable<CashoutStatusDomainResponse>((observer) => {
      console.log('[CashoutGrpcService] Request:', { taskId });
      console.log('[CashoutGrpcService] Metadata:', {
        authorization: 'Bearer [REDACTED]',
        'x-client-type': 'web',
      });

      // Create the protobuf request using the generated class
      const request = new CashoutStatusRequest();
      request.setTaskid(taskId);

      console.log('[CashoutGrpcService] Request created:', request.toObject());
      console.log(
        '[CashoutGrpcService] Request has serializeBinary:',
        typeof request.serializeBinary,
      );

      const metadata = this.createMetadata();

      // Log the exact path being called
      const serviceName = 'com.mzic.proto.CashoutService';
      const methodName = 'monitorCashoutStatus';
      const path = `/${serviceName}/${methodName}`;
      console.log('[CashoutGrpcService] gRPC path:', path);

      // Create the gRPC streaming call using the generated service definition
      const methodDescriptor = {
        methodName: 'monitorCashoutStatus',
        service: { serviceName: 'com.mzic.proto.CashoutService' },
        requestStream: false,
        responseStream: true,
        requestType: CashoutStatusRequest,
        responseType: CashoutStatusResponse,
      };

      const client = grpc.invoke(methodDescriptor as any, {
        request: request,
        host: this.grpcHost,
        metadata: metadata,
        debug: true, // Enable debug mode
        onMessage: (message: any) => {
          console.log('[CashoutGrpcService] Raw message received:', message);
          console.log('[CashoutGrpcService] Message type:', typeof message);
          console.log(
            '[CashoutGrpcService] Message constructor:',
            message?.constructor?.name,
          );

          try {
            // The message should be a CashoutStatusResponse protobuf object
            const obj = message.toObject();
            console.log('[CashoutGrpcService] Received proto message:', obj);

            // Convert proto response to domain response
            const response: CashoutStatusDomainResponse = {
              taskId: message.getTaskid(),
              status: this.mapStatusEnum(message.getStatus()),
              reasonForRejection: message.getReasonforrejection() || undefined,
              transactionHash: message.getTransactionhash() || undefined,
            };

            console.log('[CashoutGrpcService] Mapped response:', response);
            observer.next(response);
          } catch (error) {
            console.error(
              '[CashoutGrpcService] Error processing message:',
              error,
            );
            console.error(
              '[CashoutGrpcService] Raw message that caused error:',
              message,
            );
          }
        },
        onEnd: (code: grpc.Code, message: string, trailers?: grpc.Metadata) => {
          console.log('[CashoutGrpcService] Stream ended:', {
            code,
            message,
            codeName: this.getGrpcCodeName(code),
            trailers: trailers ? this.metadataToObject(trailers) : undefined,
          });

          if (code === grpc.Code.OK) {
            observer.complete();
          } else {
            // Handle gRPC errors similar to Flutter's error handling
            const error = this.mapGrpcError(code, message);
            observer.error(error);
          }
        },
      });

      // Return cleanup function
      return () => {
        console.log('[CashoutGrpcService] Cleaning up gRPC stream');
        client.close();
      };
    });
  }

  /**
   * Map gRPC error codes to meaningful errors
   * Similar to Flutter's error mapping
   */
  private mapGrpcError(code: grpc.Code, message: string): Error {
    const codeName = this.getGrpcCodeName(code);
    switch (code) {
      case grpc.Code.Unavailable:
        return new Error(`Service unavailable: ${message}`);
      case grpc.Code.Unauthenticated:
        return new Error(`Authentication failed: ${message}`);
      case grpc.Code.DeadlineExceeded:
        return new Error(`Request timeout: ${message}`);
      case grpc.Code.InvalidArgument:
        return new Error(`Invalid request: ${message}`);
      case grpc.Code.Unknown:
        return new Error(
          `Unknown error (often protocol mismatch or server error): ${message || 'No details provided'}`,
        );
      default:
        return new Error(`gRPC error (${codeName}): ${message}`);
    }
  }

  /**
   * Get human-readable name for gRPC code
   */
  private getGrpcCodeName(code: grpc.Code): string {
    const codeNames: { [key: number]: string } = {
      0: 'OK',
      1: 'CANCELLED',
      2: 'UNKNOWN',
      3: 'INVALID_ARGUMENT',
      4: 'DEADLINE_EXCEEDED',
      5: 'NOT_FOUND',
      6: 'ALREADY_EXISTS',
      7: 'PERMISSION_DENIED',
      8: 'RESOURCE_EXHAUSTED',
      9: 'FAILED_PRECONDITION',
      10: 'ABORTED',
      11: 'OUT_OF_RANGE',
      12: 'UNIMPLEMENTED',
      13: 'INTERNAL',
      14: 'UNAVAILABLE',
      15: 'DATA_LOSS',
      16: 'UNAUTHENTICATED',
    };
    return codeNames[code] || `UNKNOWN_CODE_${code}`;
  }

  /**
   * Convert metadata to plain object for debugging
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private metadataToObject(_metadata: grpc.Metadata): Record<string, unknown> {
    const obj: Record<string, unknown> = {};
    // Note: grpc.Metadata doesn't have a standard iteration method
    // This is a simplified representation
    return obj;
  }
}
